/**
 * Admin — Feedback inbox.
 * Lists every submission from POST /api/feedback. Admin coaches only.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChatDots,
  EnvelopeSimple,
  MegaphoneSimple,
  Spinner,
  User,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function CoachAdminFeedback() {
  const { coach } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coach || coach.role !== "admin") return;
    let alive = true;
    api.get("/admin/feedback")
      .then(({ data }) => { if (alive) setItems(data?.feedback || []); })
      .catch((e) => { if (alive) toast.error(apiError(e, "Could not load feedback")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [coach]);

  if (coach === null) {
    return (
      <div className="text-center text-[#8A948E] py-16 flex items-center justify-center gap-2">
        <Spinner size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!coach) return <Navigate to="/coach/login" state={{ from: "/coach/admin/feedback" }} replace />;
  if (coach.role !== "admin") return <Navigate to="/coach/dashboard" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
      <div className="anim-in delay-1">
        <Link
          to="/coach/admin"
          data-testid="admin-feedback-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#8A948E] hover:text-[#1E3A2B] mb-3"
        >
          <ArrowLeft size={14} weight="bold" /> Admin Console
        </Link>
        <div className="label-kicker" style={{ color: "#C77D5E" }}>Feedback inbox</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          What users are telling us.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Every submission from the site-wide "Please… PLEASE!" prompt.
          Newest first. {items.length} total.
        </p>
      </div>

      {loading ? (
        <div className="mt-10 text-[#8A948E] flex items-center gap-2">
          <Spinner size={16} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-10 bg-[#F3F0EA] border border-[#E8E4DB] rounded-2xl p-10 text-center" data-testid="admin-feedback-empty">
          <MegaphoneSimple size={32} weight="duotone" className="text-[#C77D5E] inline-block" />
          <p className="mt-3 text-[#4A524C]">No feedback yet.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="admin-feedback-list">
          {items.map((f) => (
            <div
              key={f.id}
              data-testid={`admin-feedback-row-${f.id}`}
              className="bg-white border border-[#E8E4DB] rounded-2xl p-5"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#F3F0EA] text-[#1E3A2B] flex items-center justify-center flex-shrink-0">
                    <User size={14} weight="duotone" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1E3A2B]">
                      {f.name || <span className="text-[#8A948E]">Anonymous</span>}
                    </div>
                    {f.email && (
                      <a
                        href={`mailto:${f.email}?subject=${encodeURIComponent("Re: Your feedback on Conflict Coach")}`}
                        className="text-xs text-[#4A524C] hover:text-[#1E3A2B] underline decoration-[#1E3A2B]/30 inline-flex items-center gap-1"
                      >
                        <EnvelopeSimple size={11} weight="duotone" /> {f.email}
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-xs text-[#8A948E] text-right">
                  <div>{f.created_at ? new Date(f.created_at).toLocaleString() : "—"}</div>
                  {f.page && <div className="font-mono truncate max-w-[220px]">{f.page}</div>}
                  {(f.leader_id || f.coach_id) && (
                    <div>
                      {f.coach_id ? "coach" : "leader"} · {(f.coach_id || f.leader_id).slice(0, 8)}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-4 text-[#121815] leading-relaxed whitespace-pre-wrap border-l-2 border-[#1E3A2B] pl-4">
                {f.message}
              </p>
              <div className="mt-2 text-[10px] text-[#8A948E] flex items-center gap-1">
                <ChatDots size={11} weight="duotone" /> ref {String(f.id).slice(0, 8).toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
